// src/features/system/devices/components/iot/IoTValidationPanel.tsx
// ────────────────────────────────────────────────────────────────────────────
// Device Validation Run panel — select a device, trigger the 5-probe
// validation sequence, and view history + baseline results.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchPresenceApi,
  DEVICE_KEYS,
} from '../../api/iotDevicesApi';
import { useDevicesList } from '../../hooks/useDevicesList';
import { useDeviceValidation } from '../../hooks/useDeviceValidation';
import type {
  ValidationOutcomeRow,
  ValidationProbe,
  ValidationBaseline,
} from '../../api/deviceValidationApi';
import MinimalisticTable, { type ColumnDef } from '@/shared/components/ui/MinimalisticTable';

// ── Helpers ──────────────────────────────────────────────────────────────

function ProbeStatus({ passed }: { passed: boolean }) {
  return passed
    ? <span className="text-emerald-400 font-medium text-xs">PASS</span>
    : <span className="text-rose-400 font-medium text-xs">FAIL</span>;
}

function StatusBadge({ status }: { status: string }) {
  const cls = status === 'PASS'
    ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
    : 'bg-rose-500/20 text-rose-400 border-rose-500/30';
  return (
    <span className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold ${cls}`}>
      {status}
    </span>
  );
}

function fmt(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'number') return String(v);
  if (typeof v === 'string') return v;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso ?? '—';
  }
}

// ── Component ────────────────────────────────────────────────────────────

export default function IoTValidationPanel() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [deviceIp, setDeviceIp]     = useState('');

  // ── Devices list for selector ──────────────────────────────────────────
  const { data: devices = [] } = useDevicesList({ staleTime: 60_000 });

  const { data: presence = [] } = useQuery({
    queryKey: DEVICE_KEYS.presence,
    queryFn:  fetchPresenceApi,
    staleTime: 30_000,
  });

  const presenceMap = new Map(presence.map(p => [p.device_id, p]));

  // Derive serial_number from device metadata (label or device_id)
  const selectedDevice = devices.find(d => d.device_id === selectedId) ?? null;

  // ── Validation hook ────────────────────────────────────────────────────
  const { history, snapshot, start } = useDeviceValidation(selectedId);

  const historyRows = history.data ?? [];

  // ── Start run handler ──────────────────────────────────────────────────
  const handleStart = () => {
    if (!selectedId || !deviceIp.trim()) return;
    start.mutate({
      device_id:     selectedId,
      device_ip:     deviceIp.trim(),
      serial_number: selectedDevice?.label ?? selectedId,
    });
  };

  // ── Probe table for the latest run ─────────────────────────────────────
  const latestRun = historyRows[0] as (ValidationOutcomeRow & { probes?: ValidationProbe[]; baseline?: ValidationBaseline | null }) | undefined;

  // Try to get probes from hot-stream snapshot for richer detail
  const probesFromSnapshot: ValidationProbe[] = (() => {
    const events = snapshot.data ?? [];
    const probeEvent = events.find(e => e.v_jsonb && 'probes' in e.v_jsonb);
    if (probeEvent?.v_jsonb && Array.isArray((probeEvent.v_jsonb as Record<string, unknown>).probes)) {
      return (probeEvent.v_jsonb as Record<string, unknown>).probes as ValidationProbe[];
    }
    return [];
  })();

  const probeColumns: ColumnDef<ValidationProbe>[] = [
    { header: 'Probe',    accessor: 'probe_id' },
    {
      header: 'Status',
      accessor: 'passed',
      Cell: (v: boolean) => <ProbeStatus passed={v} />,
    },
    { header: 'Message',  accessor: 'message' },
    {
      header: 'Measured',
      accessor: 'measured',
      Cell: (v: unknown) => <span className="text-xs font-mono">{fmt(v)}</span>,
    },
    {
      header: 'Reference',
      accessor: 'reference',
      Cell: (v: unknown) => <span className="text-xs font-mono">{fmt(v)}</span>,
    },
    {
      header: 'Duration',
      accessor: 'duration_ms',
      Cell: (v: number) => `${v} ms`,
    },
  ];

  // ── History table columns ──────────────────────────────────────────────
  const historyColumns: ColumnDef<ValidationOutcomeRow>[] = [
    {
      header: 'Status',
      accessor: 'validation_status',
      Cell: (v: string) => <StatusBadge status={v} />,
    },
    { header: 'Device ID',     accessor: 'device_id' },
    { header: 'Serial',        accessor: 'serial_number' },
    { header: 'Passed',        accessor: 'probes_passed' },
    { header: 'Failed',        accessor: 'probes_failed' },
    { header: 'Total',         accessor: 'probes_total' },
    { header: 'Validated By',  accessor: 'validated_by' },
    {
      header: 'Validated At',
      accessor: 'validated_at',
      Cell: (v: string) => <span className="text-xs text-neutral-400">{fmtDate(v)}</span>,
    },
  ];

  return (
    <div className="h-full flex flex-col gap-4 overflow-hidden">
      {/* ── Device selector + IP input + trigger ────────────────── */}
      <div className="flex items-center gap-3 shrink-0 flex-wrap">
        <label className="text-xs text-neutral-400">Device:</label>
        <select
          value={selectedId ?? ''}
          onChange={e => setSelectedId(e.target.value || null)}
          className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm
                     focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          <option value="">— select device —</option>
          {devices.map(d => (
            <option key={d.device_id} value={d.device_id}>
              {d.label ? `${d.label} (${d.device_id})` : d.device_id}
              {presenceMap.get(d.device_id)?.online ? ' ●' : ''}
            </option>
          ))}
        </select>

        <label className="text-xs text-neutral-400 ml-2">Device IP:</label>
        <input
          type="text"
          value={deviceIp}
          onChange={e => setDeviceIp(e.target.value)}
          placeholder="192.168.x.x"
          className="px-2 py-1 rounded pac-glass-input text-neutral-100 text-sm w-36
                     focus:outline-none focus:ring-1 focus:ring-sky-500"
        />

        <button
          onClick={handleStart}
          disabled={!selectedId || !deviceIp.trim() || start.isPending}
          className="px-3 py-1 rounded text-sm font-medium
                     bg-sky-600 hover:bg-sky-500 disabled:opacity-40
                     disabled:cursor-not-allowed text-white transition-colors"
        >
          {start.isPending ? 'Running…' : 'Run Validation'}
        </button>

        {start.isError && (
          <span className="text-rose-400 text-xs ml-2">
            {(start.error as Error).message}
          </span>
        )}
        {start.isSuccess && (
          <span className="text-emerald-400 text-xs ml-2">
            Validation started
          </span>
        )}
      </div>

      {!selectedId && (
        <div className="flex-1 grid place-items-center text-neutral-600 text-sm">
          Select a device to run validation or view history.
        </div>
      )}

      {selectedId && (
        <div className="flex-1 min-h-0 overflow-auto flex flex-col gap-6">
          {/* ── Summary cards ──────────────────────────────────── */}
          <div className="flex gap-3 shrink-0">
            <SummaryCard label="Runs" value={historyRows.length} />
            <SummaryCard
              label="Last Status"
              value={latestRun?.validation_status ?? '—'}
              accent={latestRun?.validation_status === 'PASS' ? 'emerald' : latestRun ? 'rose' : 'neutral'}
            />
            <SummaryCard
              label="Pass Rate"
              value={historyRows.length
                ? `${Math.round(historyRows.filter(r => r.validation_status === 'PASS').length / historyRows.length * 100)}%`
                : '—'}
              accent="sky"
            />
          </div>

          {/* ── Probe results (latest) ────────────────────────── */}
          {probesFromSnapshot.length > 0 && (
            <section>
              <h3 className="text-sm font-semibold text-neutral-300 mb-2">
                Latest Probe Results
              </h3>
              <MinimalisticTable<ValidationProbe>
                columns={probeColumns}
                data={probesFromSnapshot}
                showNewButton={false}
                noDataMessage="No probe data available."
              />
            </section>
          )}

          {/* ── Baseline snapshot ─────────────────────────────── */}
          {latestRun?.baseline && (
            <section>
              <h3 className="text-sm font-semibold text-neutral-300 mb-2">
                Reference Baseline
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                <BaselineCard label="Temperature" value={latestRun.baseline.temperature_c != null ? `${latestRun.baseline.temperature_c.toFixed(1)} °C` : '—'} />
                <BaselineCard label="Humidity"    value={latestRun.baseline.humidity_pct  != null ? `${latestRun.baseline.humidity_pct.toFixed(0)} %` : '—'} />
                <BaselineCard label="RSSI"        value={latestRun.baseline.rssi_dbm     != null ? `${latestRun.baseline.rssi_dbm} dBm` : '—'} />
                <BaselineCard label="Free Heap"   value={latestRun.baseline.free_heap    != null ? `${latestRun.baseline.free_heap.toLocaleString()} B` : '—'} />
                <BaselineCard label="FW Version"  value={latestRun.baseline.fw_version ?? '—'} />
              </div>
            </section>
          )}

          {/* ── Validation history table ──────────────────────── */}
          <section className="flex-1 min-h-0">
            <h3 className="text-sm font-semibold text-neutral-300 mb-2">
              Validation History
            </h3>
            <MinimalisticTable<ValidationOutcomeRow>
              columns={historyColumns}
              data={historyRows}
              showNewButton={false}
              noDataMessage={history.isLoading ? 'Loading history…' : 'No validation runs yet.'}
            />
          </section>
        </div>
      )}
    </div>
  );
}

// ── Small display components ─────────────────────────────────────────────

function SummaryCard({ label, value, accent = 'sky' }: {
  label: string;
  value: number | string;
  accent?: 'sky' | 'emerald' | 'rose' | 'neutral';
}) {
  const ring: Record<string, string> = {
    sky:     'border-sky-500/30',
    emerald: 'border-emerald-500/30',
    rose:    'border-rose-500/30',
    neutral: 'border-neutral-700',
  };
  const text: Record<string, string> = {
    sky:     'text-sky-400',
    emerald: 'text-emerald-400',
    rose:    'text-rose-400',
    neutral: 'text-neutral-500',
  };
  return (
    <div className={`px-4 py-2 rounded-lg border ${ring[accent]} pac-glass`}>
      <div className={`text-2xl font-bold tabular-nums ${text[accent]}`}>{value}</div>
      <div className="text-[11px] text-neutral-500 uppercase tracking-wide">{label}</div>
    </div>
  );
}

function BaselineCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="px-3 py-2 rounded-lg border border-neutral-700/50 pac-glass">
      <div className="text-sm font-semibold text-neutral-200 tabular-nums">{value}</div>
      <div className="text-[10px] text-neutral-500 uppercase tracking-wider">{label}</div>
    </div>
  );
}
